import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import {
  adjustSupply,
  replenishSupply,
} from '../../../../../features/supplies/suppliesThunks';
import {
  EMPTY_ADJUST_FORM,
  EMPTY_REPLENISH_FORM,
  adjustProblem,
  buildAdjustPayload,
  buildReplenishPayload,
  formatMoney,
  projectedAverageCost,
  replenishProblem,
} from '../../../../../features/supplies/suppliesUtils';
import { formatQuantity } from '../../../../../utils/inventoryLabels';

export const SUPPLY_MOVEMENT = { REPLENISH: 'replenish', ADJUST: 'adjust' };

/**
 * The two ways the balance of a supply moves, which are not the same act:
 *
 * - **Reponer** is a purchase coming in. It carries a cost, and it is the only
 *   thing that recalculates the weighted average — shown before saving, so a
 *   purchase at another price does not come as a surprise.
 * - **Ajustar** corrects the balance without touching the cost: losing boxes
 *   does not change what they cost. Its reason is mandatory.
 *
 * @param {object} props
 * @param {'replenish'|'adjust'|null} props.mode - `null` keeps it closed.
 * @param {object|null} props.supply - The supply being moved.
 * @param {() => void} props.onClose
 * @param {(message: string) => void} props.onDone - Told what happened, in a sentence.
 */
export const SupplyMovementModal = ({ mode, supply, onClose, onDone }) => {
  const dispatch = useDispatch();
  const saving = useSelector((state) => state.supplies.saving);

  const [replenishForm, setReplenishForm] = useState(EMPTY_REPLENISH_FORM);
  const [adjustForm, setAdjustForm] = useState(EMPTY_ADJUST_FORM);
  const [saveError, setSaveError] = useState(null);

  const isOpen = Boolean(mode && supply);
  const isReplenish = mode === SUPPLY_MOVEMENT.REPLENISH;

  // Every opening starts empty.
  useEffect(() => {
    if (!isOpen) return;
    setReplenishForm(EMPTY_REPLENISH_FORM);
    setAdjustForm(EMPTY_ADJUST_FORM);
    setSaveError(null);
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const problem = isReplenish ? replenishProblem(replenishForm) : adjustProblem(supply, adjustForm);
  const projected = isReplenish ? projectedAverageCost(supply, replenishForm) : null;

  const handleSave = async () => {
    setSaveError(null);
    const action = await dispatch(
      isReplenish
        ? replenishSupply({ supplyId: supply.id, values: buildReplenishPayload(replenishForm) })
        : adjustSupply({ supplyId: supply.id, values: buildAdjustPayload(adjustForm) })
    );

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }

    onDone(
      isReplenish
        ? `Entraron ${formatQuantity(replenishForm.quantity)} de ${supply.sku}. El costo promedio quedó en ${formatMoney(
            action.payload.average_cost
          )}.`
        : `${supply.sku} ajustado en ${Number(adjustForm.quantity) > 0 ? '+' : ''}${formatQuantity(
            adjustForm.quantity
          )}. Quedan ${formatQuantity(action.payload.quantity)}.`
    );
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isReplenish ? 'Reponer stock' : 'Ajustar stock'}
      testId="supply-movement-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {supply.name} · saldo actual {formatQuantity(supply.quantity)}
      </p>

      <div className="space-y-4">
        {saveError && (
          <div
            data-testid="supply-movement-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        {isReplenish ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Cantidad que ingresa" htmlFor="supply-replenish-quantity">
                <input
                  id="supply-replenish-quantity"
                  type="number"
                  min="0"
                  step="any"
                  data-testid="supply-replenish-quantity"
                  value={replenishForm.quantity}
                  onChange={(event) =>
                    setReplenishForm({ ...replenishForm, quantity: event.target.value })
                  }
                  disabled={saving}
                  className={inputClass}
                />
              </Field>
              <Field label="Costo unitario (neto)" htmlFor="supply-replenish-cost">
                <input
                  id="supply-replenish-cost"
                  type="number"
                  min="0"
                  step="any"
                  data-testid="supply-replenish-cost"
                  value={replenishForm.unit_cost}
                  onChange={(event) =>
                    setReplenishForm({ ...replenishForm, unit_cost: event.target.value })
                  }
                  disabled={saving}
                  className={inputClass}
                />
              </Field>
            </div>

            {projected !== null && (
              <div
                data-testid="supply-projected-cost"
                className="bg-blue-50 dark:bg-blue-900/20 text-info p-3 rounded-xl border border-blue-200 dark:border-blue-900 text-sm"
              >
                Nuevo costo promedio: <strong>{formatMoney(projected)}</strong> · actual{' '}
                {Number(supply.average_cost) ? formatMoney(supply.average_cost) : 'sin costo'}
              </div>
            )}

            <Field
              label="Proveedor / N° de guía o factura"
              htmlFor="supply-replenish-reference"
              hint="Ej: Cartones Sur · Guía 4581"
            >
              <input
                id="supply-replenish-reference"
                type="text"
                data-testid="supply-replenish-reference"
                value={replenishForm.reference}
                onChange={(event) =>
                  setReplenishForm({ ...replenishForm, reference: event.target.value })
                }
                disabled={saving}
                className={inputClass}
              />
            </Field>

            <p className="text-xs text-text-muted">
              Queda en el historial con fecha, cantidad, costo, referencia y usuario. El costo
              promedio se recalcula con cada ingreso, y es el que valoriza el stock y los consumos.
            </p>
          </>
        ) : (
          <>
            <Field
              label="Ajuste (+ / −)"
              htmlFor="supply-adjust-quantity"
              hint="La diferencia, con su signo: −5 si faltan cinco, 5 si aparecieron."
            >
              <input
                id="supply-adjust-quantity"
                type="number"
                step="any"
                data-testid="supply-adjust-quantity"
                value={adjustForm.quantity}
                onChange={(event) => setAdjustForm({ ...adjustForm, quantity: event.target.value })}
                disabled={saving}
                className={inputClass}
              />
            </Field>
            <Field
              label="Motivo del ajuste"
              htmlFor="supply-adjust-notes"
              hint="Ej: merma por inventario. Sin motivo, la diferencia no se puede reconstruir después."
            >
              <input
                id="supply-adjust-notes"
                type="text"
                data-testid="supply-adjust-notes"
                value={adjustForm.notes}
                onChange={(event) => setAdjustForm({ ...adjustForm, notes: event.target.value })}
                disabled={saving}
                className={inputClass}
              />
            </Field>
            <p className="text-xs text-text-muted">
              El ajuste corrige el saldo y no toca el costo promedio: perder cajas no cambia lo que
              costaron.
            </p>
          </>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing. */}
        <Button
          variant="primary"
          loading={saving}
          loadingLabel="Guardando..."
          disabled={Boolean(problem)}
          onClick={handleSave}
          data-testid="supply-movement-save"
        >
          {problem || (isReplenish ? 'Registrar reposición' : 'Registrar ajuste')}
        </Button>
      </div>
    </Modal>
  );
};
